// @services/clients/ClientProjectClient.ts
import type { ClientProjectClient as InteropClientProjectClient } from '@ps-bridge/clients/clientProject';
import type {
    ClientListResponse,
    ClientProjectError,
    CreateClientRequest,
    CreateProjectRequest,
    PresignProjectDocumentsRequest,
    ProjectDocumentListResponse,
    ProjectDocumentPresignResponse,
    ProjectListResponse,
    UpdateProjectRequest,
} from '@ps-bridge/models/core/dataContracts';
import type { EffectTs } from '@ps-bridge/interop';
import { effectFromRaw } from '@utils/effects/branded';
import type { Ok } from '@ps-bridge/ok';

export interface ClientProjectClient {
    readonly listClients: EffectTs<ClientListResponse, ClientProjectError>;
    readonly createClient: (request: CreateClientRequest) => EffectTs<Ok, ClientProjectError>;
    readonly listProjects: (clientId: string) => EffectTs<ProjectListResponse, ClientProjectError>;
    readonly createProject: (clientId: string, request: CreateProjectRequest) => EffectTs<Ok, ClientProjectError>;
    readonly updateProject: (projectId: string, request: UpdateProjectRequest) => EffectTs<Ok, ClientProjectError>;
    readonly deleteProject: (projectId: string) => EffectTs<Ok, ClientProjectError>;
    readonly listProjectDocuments: (
        projectId: string,
    ) => EffectTs<ProjectDocumentListResponse, ClientProjectError>;
    readonly presignProjectDocuments: (
        clientId: string,
        projectId: string,
        request: PresignProjectDocumentsRequest,
    ) => EffectTs<ProjectDocumentPresignResponse, ClientProjectError>;
    readonly deleteProjectDocument: (projectId: string, documentId: string) => EffectTs<Ok, ClientProjectError>;
}

export const fromInteropClientProjectClient = (
    client: InteropClientProjectClient,
): ClientProjectClient => ({
    listClients: effectFromRaw(client.listClients),
    createClient: (request) => effectFromRaw(client.createClient(request)),
    listProjects: (clientId) => effectFromRaw(client.listProjects(clientId)),
    createProject: (clientId, request) => effectFromRaw(client.createProject(clientId)(request)),
    updateProject: (projectId, request) => effectFromRaw(client.updateProject(projectId)(request)),
    deleteProject: (projectId) => effectFromRaw(client.deleteProject(projectId)),
    listProjectDocuments: (projectId) => effectFromRaw(client.listProjectDocuments(projectId)),
    presignProjectDocuments: (clientId, projectId, request) =>
        effectFromRaw(client.presignProjectDocuments(clientId)(projectId)(request)),
    deleteProjectDocument: (projectId, documentId) =>
        effectFromRaw(client.deleteProjectDocument(projectId)(documentId)),
});
